import {
  Area,
  ComposedChart,
  Line,
  CartesianGrid,
  Tooltip,
  XAxis,
  YAxis,
  ResponsiveContainer,
} from "recharts";
import { Zap } from "lucide-react";
import { SectionPanel } from "@/components/ui/section-panel";
import {
  splitForecast,
  nowKeyOf,
  type OperationalLoopPoint,
} from "./operational-loop-data";
import {
  CHART_MARGIN,
  hoverHandlers,
  loopXAxisProps,
  loopYAxis,
  LOOP_STAGE_ACCENT,
  type HoverHandler,
} from "./chart-shared";
import { nowMarker } from "./NowMarker";
import { applyMarkers } from "./ApplyMarkers";
import { ForecastTooltip } from "./ForecastTooltip";
import { joinFi, type ApplyMarker } from "./operational-loop-adapter";

// 4. Vaikutus — the recipe's effect against the perusura it replaces. Only the term the
// building service can estimate without a counterfactual is drawn: fan electrical power
// (`report.impact`, affinity law P ∝ Q³). Offset-native ventilation_level_offset is % of
// *scheduled* fan power (offset 0 → 100, not a stopped fan); leftover absolute fan-speed
// recipes are the same series as % of nameplate. Measured over the elapsed part of the
// recipe window and estimated (commanded target held forward) over the remaining part —
// the backend's own speed_at/baseline_at split (task #1884), read here through the same
// Historia/Ennuste convention every other loop chart uses. The heating/cooling/heat-recovery
// side is absent — their baseline needs the what-if evaluator — and the panel names them
// from the backend's `not_computable_terms` rather than a hardcoded list. An equipment
// without a fan port (KLA) keeps the card empty with that same reason.
export function ImpactChart({
  points,
  markers,
  loading,
  real,
  hasFan,
  hasKwh,
  notComputableLabels,
  onHover,
}: {
  points: OperationalLoopPoint[];
  markers: ApplyMarker[];
  loading?: boolean;
  /** False when the backend returned no impact series. */
  real: boolean;
  /** False when the equipment type has no writable fan-speed port at all. */
  hasFan: boolean;
  /** True only when the backend sent the `fan_electrical_kwh` pair (nameplate configured
   *  *and* an absolute speed port) — offset-native impact stays on % of scheduled power. */
  hasKwh: boolean;
  /** FI display names of `report.not_computable_terms` (operational-loop-adapter.ts
   *  `notComputableLabelsOf`) — never a hardcoded frontend list. */
  notComputableLabels: string[];
  onHover?: HoverHandler;
}) {
  const forecastRows = splitForecast(points, ["elecKw", "elecKwh"]);
  const rows = forecastRows.map((row, i) => ({
    ...row,
    totalBaselineKw: points[i].totalBaselineKw,
    totalBaselineKwh: points[i].totalBaselineKwh,
  }));
  const nowKey = nowKeyOf(points);
  const firstKey = rows[0]?.key ?? nowKey;
  const notComputableList = notComputableLabels.length
    ? joinFi(notComputableLabels)
    : null;
  const notComputableClause = notComputableList
    ? `${notComputableList}: ei vielä laskettavissa`
    : null;

  return (
    <SectionPanel
      icon={Zap}
      title="Yhteisvaikutus"
      subtitle={
        loading
          ? "Ladataan…"
          : !hasFan
            ? `Vaikutus: ei laskettavissa tälle laitteelle${notComputableClause ? ` · ${notComputableClause}` : ""}`
            : real
              ? `Vaikutus: resepti vs. perusura — puhallinsähkö (% ajastetusta tehosta) affiniteettilailla P ∝ Q³, mitattu kuluneelta ja arvioitu jäljellä olevalta jaksolta${notComputableClause ? ` · ${notComputableClause}` : ""}`
              : "Ei vielä laskettua vaikutusta tälle laitteelle"
      }
      accentColor={LOOP_STAGE_ACCENT.impact}
    >
      {loading ? (
        <p className="text-xs text-muted-foreground">Ladataan vaikutusta…</p>
      ) : !hasFan ? (
        <p className="text-xs text-muted-foreground">
          Laitteella ei ole puhallinnopeuden ohjausporttia
          {notComputableClause ? ` — ${notComputableClause}` : ""} — mitään
          havainnollistavaa käyrää ei piirretä sen tilalle.
        </p>
      ) : !real ? (
        <p className="text-xs text-muted-foreground">
          Vaikutusta ei ole vielä laskettu tälle jaksolle.
        </p>
      ) : (
        <>
          <div className="h-48 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart
                data={rows}
                margin={CHART_MARGIN}
                syncId="operational-loop"
                {...hoverHandlers(onHover)}
              >
                <CartesianGrid
                  strokeDasharray="3 3"
                  vertical={false}
                  stroke="var(--border)"
                  strokeOpacity={0.5}
                />
                <XAxis {...loopXAxisProps(rows)} />
                <YAxis
                  {...loopYAxis("left", "impact", { domain: [0, "auto"] })}
                />
                {hasKwh ? (
                  <YAxis
                    {...loopYAxis("right", "kwh", { domain: [0, "auto"] })}
                  />
                ) : (
                  <YAxis
                    {...loopYAxis("right", "gutter", { labeled: false })}
                  />
                )}
                <Tooltip content={<ForecastTooltip />} />

                {nowMarker({ x: nowKey, firstX: firstKey, yAxisId: "impact" })}
                {applyMarkers(markers, "impact")}

                <Line
                  yAxisId="impact"
                  type="stepAfter"
                  name="Perusura: puhallinsähkö ilman reseptiä (% ajastetusta tehosta)"
                  dataKey="totalBaselineKw"
                  stroke="var(--muted-foreground)"
                  strokeWidth={1.5}
                  strokeDasharray="3 3"
                  dot={false}
                />

                <Area
                  yAxisId="impact"
                  type="stepAfter"
                  name="Puhallinsähkö, % ajastetusta tehosta (Historia)"
                  dataKey="elecKwPast"
                  fill="#0ea5e9"
                  fillOpacity={0.35}
                  stroke="#0ea5e9"
                  strokeWidth={1.5}
                />
                <Area
                  yAxisId="impact"
                  type="stepAfter"
                  name="Puhallinsähkö, % ajastetusta tehosta (Ennuste)"
                  dataKey="elecKwFuture"
                  fill="#0ea5e9"
                  fillOpacity={0.12}
                  stroke="#0ea5e9"
                  strokeWidth={1.5}
                  strokeDasharray="5 5"
                />

                {hasKwh && (
                  <>
                    <Line
                      yAxisId="kwh"
                      type="stepAfter"
                      name="Perusura: puhallinsähkö (kWh)"
                      dataKey="totalBaselineKwh"
                      stroke="var(--muted-foreground)"
                      strokeWidth={1}
                      strokeDasharray="2 2"
                      dot={false}
                    />
                    <Line
                      yAxisId="kwh"
                      type="stepAfter"
                      name="Puhallinsähkö, kWh (Historia)"
                      dataKey="elecKwhPast"
                      stroke="#0284c7"
                      strokeWidth={1.5}
                      dot={false}
                    />
                    <Line
                      yAxisId="kwh"
                      type="stepAfter"
                      name="Puhallinsähkö, kWh (Ennuste)"
                      dataKey="elecKwhFuture"
                      stroke="#0284c7"
                      strokeWidth={1.5}
                      strokeDasharray="5 5"
                      dot={false}
                    />
                  </>
                )}
              </ComposedChart>
            </ResponsiveContainer>
          </div>
          {notComputableList && (
            <p className="mt-2 text-[10px] text-muted-foreground">
              {notComputableList[0].toUpperCase()}
              {notComputableList.slice(1)} puuttuvat tarkoituksella: niiden
              perusura vaatii what-if-laskennan, jota ei vielä ole — mitään
              havainnollistavaa käyrää ei piirretä sen tilalle, eikä
              puhallintermi toimi niiden sijaisena.
            </p>
          )}
        </>
      )}
    </SectionPanel>
  );
}
